import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  Box,
  Chip,
  Divider,
  useTheme,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import { Close as CloseIcon, Keyboard as KeyboardIcon } from '@mui/icons-material';
import { hotkeyDefinitions } from '../../context/HotkeysContext';

interface KeyboardShortcutsDialogProps {
  open: boolean;
  onClose: () => void;
}

const ShortcutKey: React.FC<{ keys: string }> = ({ keys }) => {
  const theme = useTheme();

  // Parse the keys string and render as chips
  const renderKeys = () => {
    // Handle "then" sequences
    if (keys.includes(' then ')) {
      const parts = keys.split(' then ');
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {parts.map((part, i) => (
            <React.Fragment key={i}>
              <Chip
                label={part.toUpperCase()}
                size="small"
                sx={{
                  height: 24,
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  fontFamily: 'monospace',
                  bgcolor: theme.palette.mode === 'dark' ? 'grey.800' : 'grey.200',
                }}
              />
              {i < parts.length - 1 && (
                <Typography variant="caption" color="text.secondary" sx={{ mx: 0.5 }}>
                  then
                </Typography>
              )}
            </React.Fragment>
          ))}
        </Box>
      );
    }

    // Handle modifier combinations (e.g., "⌘/Ctrl + K")
    if (keys.includes('+')) {
      const parts = keys.split('+').map((p) => p.trim());
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {parts.map((part, i) => (
            <React.Fragment key={i}>
              <Chip
                label={part}
                size="small"
                sx={{
                  height: 24,
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  fontFamily: 'monospace',
                  bgcolor: theme.palette.mode === 'dark' ? 'grey.800' : 'grey.200',
                }}
              />
              {i < parts.length - 1 && (
                <Typography variant="caption" color="text.secondary" sx={{ mx: 0.25 }}>
                  +
                </Typography>
              )}
            </React.Fragment>
          ))}
        </Box>
      );
    }

    // Single key
    return (
      <Chip
        label={keys.toUpperCase()}
        size="small"
        sx={{
          height: 24,
          fontSize: '0.75rem',
          fontWeight: 600,
          fontFamily: 'monospace',
          bgcolor: theme.palette.mode === 'dark' ? 'grey.800' : 'grey.200',
        }}
      />
    );
  };

  return renderKeys();
};

export const KeyboardShortcutsDialog: React.FC<KeyboardShortcutsDialogProps> = ({
  open,
  onClose,
}) => {
  const theme = useTheme();

  // Group shortcuts by category
  const groupedShortcuts = hotkeyDefinitions.reduce<Record<string, typeof hotkeyDefinitions>>((acc, shortcut) => {
    if (!acc[shortcut.category]) {
      acc[shortcut.category] = [];
    }
    acc[shortcut.category].push(shortcut);
    return acc;
  }, {});

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          maxHeight: '80vh',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          pb: 1,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <KeyboardIcon color="primary" />
          <Typography variant="h6" fontWeight={600}>
            Keyboard Shortcuts
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ pt: 3 }}>
        <Grid container spacing={4}>
          {Object.entries(groupedShortcuts).map(([category, shortcuts]) => (
            <Grid size={{ xs: 12, md: category === 'Navigation' ? 12 : 6 }} key={category}>
              <Typography
                variant="subtitle2"
                color="primary"
                fontWeight={600}
                sx={{ mb: 2, textTransform: 'uppercase', letterSpacing: 0.5 }}
              >
                {category}
              </Typography>

              {category === 'Navigation' ? (
                // Navigation shortcuts in grid layout
                <Grid container spacing={1}>
                  {shortcuts.map((shortcut, index) => (
                    <Grid size={{ xs: 12, sm: 6, md: 4 }} key={index}>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          py: 1,
                          px: 1.5,
                          borderRadius: 1,
                          bgcolor: theme.palette.mode === 'dark' ? 'grey.900' : 'grey.50',
                        }}
                      >
                        <Typography variant="body2" color="text.secondary">
                          {shortcut.description}
                        </Typography>
                        <ShortcutKey keys={shortcut.keys} />
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              ) : (
                // Other shortcuts in list layout
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {shortcuts.map((shortcut, index) => (
                    <Box
                      key={index}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        py: 1,
                        px: 1.5,
                        borderRadius: 1,
                        bgcolor: theme.palette.mode === 'dark' ? 'grey.900' : 'grey.50',
                      }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        {shortcut.description}
                      </Typography>
                      <ShortcutKey keys={shortcut.keys} />
                    </Box>
                  ))}
                </Box>
              )}
            </Grid>
          ))}
        </Grid>

        <Box
          sx={{
            mt: 4,
            p: 2,
            borderRadius: 1,
            bgcolor: theme.palette.mode === 'dark' ? 'primary.900' : 'primary.50',
            border: `1px solid ${theme.palette.primary.main}20`,
          }}
        >
          <Typography variant="body2" color="text.secondary">
            <strong>Pro tip:</strong> Press{' '}
            <Chip
              label="⌘/Ctrl"
              size="small"
              sx={{ height: 20, fontSize: '0.7rem', mx: 0.5 }}
            />
            <Chip
              label="K"
              size="small"
              sx={{ height: 20, fontSize: '0.7rem', mx: 0.5 }}
            />
            to open the command palette for quick access to all actions and navigation.
          </Typography>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default KeyboardShortcutsDialog;
